import React, {ReactElement} from "react"
import {AllowedIncomingRtpColumns, IncomingRtpColumns} from "./IncomingRtp.types"
import {Resource} from "../../resources/common"
import {useNavigate} from "react-router-dom"
import {BankName} from "../BankName/BankName"
import {Org} from "../../resources/org"
import {incomingRealTimePaymentTypeLabel, IncomingRealTimePayment} from "../../resources/incomingRealTimePayment"
import {DataTableCell, DataTableRow} from "../DataTable/DataTable"
import {useIsUnitUser} from "../../services/auth"
import {kebabCase, startCase} from "lodash"
import numeral from "numeral"
import moment from "moment"
import classNames from "classnames"

interface IncomingRtpRowProps {
	incomingRtp: IncomingRealTimePayment
	includedColumns: AllowedIncomingRtpColumns[]
	include?: Resource[]
	onClick?: (e: React.MouseEvent<HTMLTableRowElement>) => void
}

function OrgName({orgId, included}: {orgId: string | undefined | null; included?: Resource[]}) {
	if (included && orgId) {
		const org = included.find((r) => r.type == "org" && r.id === orgId.toString()) as Org
		if (org) {
			return <td>{org.attributes.name}</td>
		}
	}
	return <td>&mdash;</td>
}

export function IncomingRtpRow({incomingRtp, includedColumns, include, onClick}: IncomingRtpRowProps) {
	const navigate = useNavigate()
	const id = incomingRtp.id
	const org = incomingRtp.relationships.org?.data.id
	const bank = incomingRtp.relationships.bank.data.id
	const customerId = incomingRtp.relationships.customer?.data.id
	const accountId = incomingRtp.relationships.account?.data.id
	const status = incomingRtp.attributes.status
	const createdAt = incomingRtp.attributes.createdAt
	const amount = incomingRtp.attributes.amount
	const isMonetary = incomingRtp.attributes.isMonetary
	const customer =
		customerId && include
			? include.find(
					(r) => (r.type == "individualCustomer" || r.type == "businessCustomer") && r.id == customerId.toString()
			  )
			: undefined
	let customerName: string | undefined = undefined

	if (customer) {
		if (customer.type == "individualCustomer") {
			customerName = `${customer.attributes.fullName.first} ${customer.attributes.fullName.last}`
		} else if (customer.type == "businessCustomer") {
			customerName = customer.attributes.name
		}
	}

	const contentColumns: Partial<Record<AllowedIncomingRtpColumns, ReactElement>> = {}

	if (includedColumns.includes(IncomingRtpColumns.id)) {
		contentColumns["Id"] = (
			<DataTableCell>
				<span className="data-table-id">{id}</span>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(IncomingRtpColumns.org)) {
		contentColumns["Org"] = <OrgName orgId={org} included={include} />
	}

	if (useIsUnitUser() && includedColumns.includes(IncomingRtpColumns.bank) && bank) {
		contentColumns["Bank"] = (
			<DataTableCell>
				<BankName bankId={bank.toString()} included={include} />
			</DataTableCell>
		)
	}

	if (includedColumns.includes(IncomingRtpColumns.customer)) {
		contentColumns["Customer"] = (
			<DataTableCell>
				{customerName ? (
					<a
						className="link"
						onClick={(e) => {
							e.preventDefault()
							e.stopPropagation()
							if (customer) {
								navigate(`/${customer.type == "individualCustomer" ? "individual" : "business"}/${customerId}`)
							}
						}}
					>
						{customerName}
					</a>
				) : (
					<>&mdash;</>
				)}
			</DataTableCell>
		)
	}

	if (includedColumns.includes(IncomingRtpColumns.accountId)) {
		contentColumns["Account"] = (
			<DataTableCell>
				<a
					className="link"
					onClick={(e) => {
						e.preventDefault()
						e.stopPropagation()
						if (accountId) {
							navigate(`/accounts/${accountId}`)
						}
					}}
				>
					{accountId}
				</a>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(IncomingRtpColumns.amount)) {
		contentColumns["Amount"] = (
			<DataTableCell>
				<span className="incoming-rtp-amount">{numeral(amount / 100).format("$0,0.00")}</span>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(IncomingRtpColumns.status)) {
		contentColumns["Status"] = (
			<DataTableCell>
				<div className={classNames("incoming-rtp-status", kebabCase(status))}>{startCase(status)}</div>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(IncomingRtpColumns.type)) {
		contentColumns["Type"] = <DataTableCell>{incomingRealTimePaymentTypeLabel(incomingRtp.type)}</DataTableCell>
	}

	if (includedColumns.includes(IncomingRtpColumns.createdAt)) {
		contentColumns["Created At"] = <DataTableCell>{moment(createdAt).format("L LT")}</DataTableCell>
	}

	if (includedColumns.includes(IncomingRtpColumns.isMonetary)) {
		contentColumns["Monetary"] = <DataTableCell>{isMonetary ? "Yes" : "No"}</DataTableCell>
	}

	const content: Array<ReactElement | null> = []

	includedColumns.forEach((col, i) => {
		if (col in contentColumns && contentColumns[col]) {
			const column = {...contentColumns[col]} as ReactElement
			column.key = i.toString()
			content.push(column)
		}
	})

	return <DataTableRow onClick={(e) => onClick && onClick(e)}>{content}</DataTableRow>
}
